// vendors
import Vue from 'vue';
import VueModal from 'vue-js-modal';

// misc
import { i18n, loadDefaultLanguage } from '@v4/locale/async';
import { fetchConfigurator } from '@v4/services/api/v3/car';
import { fetchVersions } from '@v4/services/api/v3/configurator';
import { fetchOptionals } from '@v4/services/api/v3/equipment';
import { getJsonDataFromScript } from '@v4/common/utils/json';

// mixins
import { dkModalAware } from '@v4/common/components/mixins/modal';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';

import { NAVIGATION_ID_MAP_WITH_DEALERS, NAVIGATION_ID_MAP_WITHOUT_DEALERS } from '@v4/configuration-wizard/const';
import { EventBus } from '@v4/common/utils/event-bus';
import { getQueryParams } from '@v4/common/utils/url';
import { dealerSelectionStore } from '@v4/lead-form/store/index';

(async () => {
    Vue.use(VueModal, { dynamic: true, injectModalsContainer: true });
    await loadDefaultLanguage();

    if (!document.getElementById('configuration-wizard')) {
        return;
    }

    const fetchedData = getJsonDataFromScript('#configuration-wizard-data') || {};

    new Vue({
        el: '#configuration-wizard',
        name: 'ConfiguratorPage',
        i18n,
        components: {
            WizardHeader: () => ({
                component: import(
                    /* webpackChunkName: "WizardHeader-vue" */ '@v4/configuration-wizard/components/atoms/WizardHeader'
                ),
            }),
            WizardCta: () => ({
                component: import(
                    /* webpackChunkName: "WizardCta-vue" */ '@v4/configuration-wizard/components/atoms/WizardCta'
                ),
            }),
            CarImage: () => ({
                component: import(
                    /* webpackChunkName: "CarImage-vue" */ '@v4/configuration-wizard/components/atoms/CarImage'
                ),
            }),
            DkTabsDst: () => ({
                component: import(
                    /* webpackChunkName: "DkTabsDST-vue" */ '@v4/common/components/molecules/DkTabsDST.vue'
                ),
            }),
            DkTabDst: () => ({
                component: import(/* webpackChunkName: "DkTabDST-vue" */ '@v4/common/components/atoms/DkTabDST.vue'),
            }),
            ColorSelection: () => ({
                component: import(
                    /* webpackChunkName: "ColorSelection-vue" */ '@v4/configuration-wizard/components/molecules/ColorSelection'
                ),
            }),
            LeadFormSubmission: () => ({
                component: import(
                    /* webpackChunkName: "LeadFormSubmission-vue" */ '@v4/configuration-wizard/components/molecules/LeadFormSubmission'
                ),
            }),
            OptionalSelection: () => ({
                component: import(
                    /* webpackChunkName: "OptionalSelection-vue" */ '@v4/configuration-wizard/components/organisms/OptionalSelection'
                ),
            }),
            VersionSelection: () => ({
                component: import(
                    /* webpackChunkName: "VersionSelection-vue" */ '@v4/configuration-wizard/components/organisms/VersionSelection'
                ),
            }),
            AutoHideNav: () => ({
                component: import(
                    /* webpackChunkName: "AutoHideNav-vue", webpackPreload: true */ '@v4/common/components/atoms/AutoHideNav.vue'
                ),
            }),
            LeadForm: () => ({
                component: import(
                    /* webpackChunkName: "LeadForm-vue", webpackPreload: true */ '@v4/lead-form/LeadForm/LeadForm.vue'
                ),
            }),
            DkIcon: () => ({
                component: import(/* webpackChunkName: "DkIcon-vue" */ '@v4/common/components/atoms/DkIcon.vue'),
            }),
            DkColorPicker: () => ({
                component: import(
                    /* webpackChunkName: "DkColorPicker-vue" */ '@v4/common/components/molecules/DkColorPicker.vue'
                ),
            }),
            DkDealerInput: () => ({
                component: import(
                    /* webpackChunkName: "DkDealerInput-vue" */ '@v4/common/components/molecules/DkDealerInput.vue'
                ),
            }),
            WizardTabs: () => ({
                component: import(
                    /* webpackChunkName: "ColorSelection-vue" */ '@v4/configuration-wizard/components/molecules/WizardTabs'
                ),
            }),
        },
        mixins: [dkModalAware, gaEventHandler],
        data() {
            return {
                fetchedData,
                loading: {
                    versions: true,
                    color: true,
                    optionals: true,
                    price: true,
                },
                colorImage: '',
                error: null,
                colors: [],
                activeColor: null,
                activeTab: null,
                optionals: [],
                trims: [],
                selectedVersion: {},
                selectedTrim: {},
                selectedOptionals: [],
                selectedFakeColor: null,
                previousTab: null,
                isFirstLoad: true,
                hasDealerSelection: false,
            };
        },
        computed: {
            makeName() {
                return this.fetchedData.makeUrlCode || '';
            },
            modelName() {
                return this.fetchedData.submodelCommercialUrlCode || '';
            },
            projectId() {
                return this.fetchedData.formData.project_id ? parseInt(this.fetchedData.formData.project_id, 10) : '';
            },
            selectedDealer() {
                return dealerSelectionStore.state.selectedDealer;
            },
            tabs() {
                if (this.hasDealerSelection) {
                    return Object.keys(NAVIGATION_ID_MAP_WITH_DEALERS);
                } else {
                    return Object.keys(NAVIGATION_ID_MAP_WITHOUT_DEALERS);
                }
            },
            price() {
                const { pricing } = this.selectedVersion || {};

                let finalPrice = '';

                if (pricing && (pricing.finalPrice || pricing.originalPrice)) {
                    const price = pricing.finalPrice || pricing.originalPrice;
                    finalPrice = Number(price.replace(/[€,.]/g, '').trim()).toLocaleString(
                        document.documentElement.lang
                    );

                    if (this.activeColor) {
                        finalPrice = this.calculatePrice(finalPrice, this.activeColor.finalPrice);
                    }

                    if (this.selectedOptionals.length) {
                        const optionalsPrice = this.selectedOptionals.reduce((sum, item) => sum + item.price, 0);
                        finalPrice = this.calculatePrice(finalPrice, optionalsPrice);
                    }
                }

                return finalPrice;
            },
            isColorsTabLoading() {
                return (
                    (this.loading.color && !this.activeColor) ||
                    (this.colors.length &&
                        this.isFirstLoad &&
                        this.loading.optionals &&
                        Object.keys(this.optionals).length === 0)
                );
            },
            areOptionalsLoading() {
                return (
                    this.activeColor &&
                    !this.isFirstLoad &&
                    this.loading.optionals &&
                    Object.keys(this.optionals).length === 0
                );
            },
            isVersionsTabLoading() {
                return (
                    (this.loading.versions && this.trims.length === 0) ||
                    (this.loading.color && this.colors.length === 0)
                );
            },
            defaultImage() {
                let defaultImage =
                    this.colors && this.colors.length ? this.colors[0].colorImage.image800 : this.colorImage;

                if (!defaultImage) {
                    defaultImage = this.fetchedData.image;
                }

                return defaultImage;
            },
            tabsKey() {
                return JSON.stringify(this.tabs);
            },
            isLeadFormLoading() {
                return this.loading.price;
            },
        },
        mounted() {
            EventBus.$on('DkLeadForm:has-dealer-selection', this.handleDealerSelection);

            this.handleDefaultTab();

            if (this.activeTab === 'configuration-wizard__versions') {
                this.fetchCarConfiguration();
            }

            this.trims = this.fetchedData.trims || [];
            this.selectedTrim = this.trims.find((item) => item.selected) || this.trims[0] || {};

            const versions = this.selectedTrim.versions || [];
            const selectedVersion = versions.find((item) => item.selected) || versions[0] || {};
            this.selectedVersion = selectedVersion;

            this.fetchData();
            this.trackVisitedStep();

            EventBus.$on('WizardExitConfirmationModal:confirmExit', this.handleClickClose);
        },
        beforeDestroy() {
            EventBus.$off();
            window.removeEventListener('popstate', (e) => this.handleBrowsingBack(e));
        },
        methods: {
            handleDefaultTab() {
                if (!this.activeTab) {
                    const hashes = window.location.hash.split('#');

                    if (!hashes[0]) {
                        window.location.hash = this.tabs[0];
                    }

                    const found = this.tabs.find((tab) => (hashes.indexOf(tab) > -1 ? tab : 'null'));

                    if (found) {
                        if (found === 'configuration-wizard__price') {
                            this.activeTab = this.tabs[0];
                            window.location.hash = this.tabs[0];
                        } else {
                            this.activeTab = found;
                        }
                    } else {
                        const hasLeadFormError = !!getQueryParams().lead_error;

                        if (hasLeadFormError) {
                            this.activeTab = this.tabs[3];
                            window.location.hash = this.tabs[3];
                        } else {
                            this.activeTab = this.tabs[0];
                            window.location.hash = this.tabs[0];
                        }
                    }
                }
            },
            onTabChange({ tab }) {
                this.activeTab = tab.id;
                this.fetchData();
                this.trackVisitedStep();
            },
            onContinueBtnClick() {
                let nextTab = this.tabs[1];

                if (this.activeTab && this.tabs.indexOf(this.activeTab) + 1 <= this.tabs.length - 1) {
                    nextTab = this.tabs[this.tabs.indexOf(this.activeTab) + 1];
                }

                window.location.hash = `#${nextTab}`;
                this.activeTab = nextTab;
                this.$refs.wizardNavigation && this.$refs.wizardNavigation.selectTab(`#${nextTab}`, {});

                if (this.tabs.indexOf(this.activeTab) === this.tabs.length - 1) {
                    // The user clicks to go to the final step which is the lead form
                    this.pushGtagEvent('add_to_cart', null, this.fetchedData.events);
                }
            },
            onColorSelect(color = {}) {
                color.active = true;
                this.activeColor = color;
            },
            onVersionSelect(versionId = '') {
                const versions = this.selectedTrim.versions || [];
                const selectedVersion = versions.find((item) => item.versionId === versionId) || {};
                this.selectedVersion = selectedVersion;

                if (Object.keys(this.optionals).length) {
                    this.optionals = {};
                }
            },
            onTrimSelect(trimId = '') {
                const selectedTrim = this.trims.find((item) => item.id === trimId) || {};
                this.selectedTrim = selectedTrim;

                const versions = this.selectedTrim.versions || [];
                const selectedVersion = versions.find((item) => item.selected) || versions[0] || {};
                this.selectedVersion = selectedVersion;

                if (Object.keys(this.optionals).length) {
                    this.optionals = {};
                }
            },
            onOptionalSelect(optionals) {
                this.selectedOptionals = optionals;
            },
            fetchCarVersions() {
                this.loading.versions = true;
                fetchVersions(this.makeName, this.modelName)
                    .then(({ data }) => {
                        this.trims = data.trims;
                        this.selectedTrim = this.trims.find((trim) => trim.selected) || {};

                        const versions = this.selectedTrim.versions || [];
                        this.selectedVersion = versions.find((version) => version.selected) || {};
                    })
                    .catch((error) => {
                        this.error = error;
                    })
                    .finally(() => {
                        this.loading.versions = false;
                    });
            },
            fetchCarConfiguration() {
                this.loading.color = true;
                fetchConfigurator(this.makeName, this.modelName)
                    .then(({ data }) => {
                        this.colors = (data.colors && data.colors.external) || [];
                        this.colorImage = data.image800 || data.imageOriginal;
                    })
                    .catch((error) => {
                        this.error = error;
                    })
                    .finally(() => {
                        this.loading.color = false;
                    });
            },
            setColorConfiguration() {
                this.loading.color = true;
                this.activeColor = this.colors.find((color) => color.active);
                this.colorImage = this.defaultImage;
                this.loading.color = false;
            },
            fetchCarOptionals() {
                this.loading.optionals = true;
                const selectedVersion = this.selectedVersion || {};
                fetchOptionals(selectedVersion.versionId || '')
                    .then(({ data }) => {
                        this.optionals = data.equipments || {};
                    })
                    .catch((error) => {
                        this.error = error;
                    })
                    .finally(() => {
                        this.loading.optionals = false;
                        if (this.isFirstLoad) {
                            this.isFirstLoad = false;
                        }
                    });
            },
            trackVisitedStep() {
                this.pushGaEvent('Configurator', 'Visited step', this.tabs[this.activeTab]);

                if (this.activeTab === 'configuration-wizard__price') {
                    this.trackCheckoutEvent();
                } else {
                    this.pushGtagEvent('click_tabs_configurator', null, this.fetchedData.events, {
                        tab: this.tabs[this.activeTab],
                        // eslint-disable-next-line camelcase
                        page_source: window.location.href,
                    });
                }
            },
            trackCheckoutEvent() {
                this.pushGtagEvent('begin_checkout', null, this.fetchedData.events);
            },
            handleClickClose() {
                this.pushGtagEvent('click_close_configurator', null, this.fetchedData.events);
            },
            fetchData() {
                if (NAVIGATION_ID_MAP_WITH_DEALERS[this.activeTab] === 'versions' && this.trims.length === 0) {
                    this.fetchCarVersions();
                } else if (NAVIGATION_ID_MAP_WITH_DEALERS[this.activeTab] === 'color') {
                    if (!this.activeColor) {
                        this.setColorConfiguration();
                    }
                    if (Object.keys(this.optionals).length === 0) {
                        this.fetchCarOptionals();
                    }
                }
            },
            onFakeColorSelect(color) {
                this.selectedFakeColor = color;
            },
            calculatePrice(priceOne, priceTwo) {
                const toNumber = (str) => Number(str.replace(/[€,.]/g, '').trim());
                const parsePrice = (price) => (typeof price === 'string' ? toNumber(price) : price);
                const price1 = parsePrice(priceOne);
                const price2 = parsePrice(priceTwo);
                const total = price1 + price2;
                const totalString = total.toLocaleString(document.documentElement.lang);
                return `€ ${totalString}`;
            },
            handleDealerSelection(hasDealerSelection) {
                this.hasDealerSelection = hasDealerSelection;
                this.loading.price = false;
            },
        },
    });
})();
